import {
  ExperienceConfigurationKeys,
  SettingVisibilityEnum,
} from '@monetize/types/experience-config';
import { useCallback, useMemo } from 'react';
import { useExperienceConfiguration } from '../../../hooks';
import { useAuth } from '../../../services/auth0';
import { useFlags } from '../../../services/launchDarkly';
import { QUOTE_TEMPLATE_FIELD_TO_PERMISSION_KEY } from './quoteTemplateSettings.metadata';
import {
  getVisibleQuoteTemplateSections,
  resolveSettingVisibility,
} from './quoteTemplateSettings.utils';

/**
 * Returns a resolver that maps a quote-template setting field name to the
 * effective visibility (Editable / Read Only / Hidden) for the current user.
 *
 * Enforcement only applies when a rep is customizing an individual quote
 * (`source === 'quote'`). On the tenant settings page the admin always sees
 * every setting as Editable.
 */
export function useQuoteTemplateSettingVisibility(source: 'tenant' | 'quote') {
  const config = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteTemplateSettings,
  );
  const { currentTenantUser } = useAuth();

  const userRoleNames = useMemo(
    () => (currentTenantUser?.roles ?? []).map((role) => role.name),
    [currentTenantUser],
  );

  return useCallback(
    (fieldName: string): SettingVisibilityEnum => {
      if (source !== 'quote') {
        return SettingVisibilityEnum.EDITABLE;
      }
      const permissionKey =
        QUOTE_TEMPLATE_FIELD_TO_PERMISSION_KEY[fieldName] ?? fieldName;
      return resolveSettingVisibility(config, permissionKey, userRoleNames);
    },
    [config, source, userRoleNames],
  );
}

/**
 * Whether the current user can customize anything on a quote, and which
 * sections still have visible settings. Drives the "Customize Settings" /
 * "Customize PDF" entry points: when every setting resolves to HIDDEN for the
 * user, the entry point is hidden entirely.
 */
export function useQuoteTemplateCustomizationAccess(
  isAmendmentV2Quote: boolean,
) {
  const visibilityFor = useQuoteTemplateSettingVisibility('quote');
  const { hideBillingSchedule } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const flags = useFlags();

  return useMemo(() => {
    const visibleSections = getVisibleQuoteTemplateSections(visibilityFor, {
      isAmendmentV2Quote,
      hideBillingSchedule,
      flags,
    });
    return {
      visibleSections,
      hasAnyVisibleSetting: visibleSections.size > 0,
    };
  }, [visibilityFor, isAmendmentV2Quote, hideBillingSchedule, flags]);
}
